import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Zap, ShieldCheck, CreditCard } from 'lucide-react';
type Plan = 'pro' | 'elite';
const CHECKOUT_URLS: Record<Plan, string> = {
  pro: 'https://profelisto.com.ar/checkouts/cn/hWNDA9jJl4PvMrLet6AKwh0c/es-ar?_r=AQABRq_AsB1tZva5u-906gesRAE9dreODWBW4DnIvOQBTVc',
  elite: 'https://profelisto.com.ar/checkouts/cn/hWNDA9jVWnctpzSUWajxkUC2/es-ar?_r=AQABQ3PQqH4YR6r5RzuMKJINi1MtJrLJYXyyTzjyQkBjX0A'
};
export function PricingSection() {
  // Countdown logic
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 37
  });
  const [selected, setSelected] = useState<Plan>('elite');
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return {
          hours: 23,
          minutes: 59,
          seconds: 59
        }; // Loop for demo purposes
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (num: number) => num.toString().padStart(2, '0');
  const ctaLabel = selected === 'elite' ? 'SÍ, QUIERO EL MEGAPACK ÉLITE →' : 'QUIERO EL PACK PROFESIONAL →';
  return <div id="precio" data-id="element-202">
      {/* Block 8: Countdown Header */}
      <section className="bg-[#B91C1C] text-white py-12 px-4 text-center" data-id="element-203">
        <div className="max-w-4xl mx-auto" data-id="element-204">
          <h2 className="text-2xl md:text-4xl font-bold mb-6 uppercase tracking-wide" data-id="element-205">
            ⏰ PRECIO DE LANZAMIENTO — OFERTA POR TIEMPO LIMITADO
          </h2>
          <div className="flex justify-center items-center gap-2 md:gap-4 text-4xl md:text-7xl font-bold font-['Anton'] tracking-wider mb-6" data-id="element-206">
            <div className="bg-black/20 px-4 py-2 rounded-lg min-w-[80px] md:min-w-[120px]" data-id="element-207">
              {formatTime(timeLeft.hours)}
            </div>
            <span data-id="element-208">:</span>
            <div className="bg-black/20 px-4 py-2 rounded-lg min-w-[80px] md:min-w-[120px]" data-id="element-209">
              {formatTime(timeLeft.minutes)}
            </div>
            <span data-id="element-210">:</span>
            <div className="bg-black/20 px-4 py-2 rounded-lg min-w-[80px] md:min-w-[120px]" data-id="element-211">
              {formatTime(timeLeft.seconds)}
            </div>
          </div>
          <p className="text-xl md:text-2xl font-bold uppercase" data-id="element-212">
            🔴 El precio sube cuando termina el contador
          </p>
        </div>
      </section>

      {/* Block 9: Checkout Selector */}
      <section className="bg-[#F9F9F9] py-16 md:py-20 px-4" data-id="element-213">
        <div className="max-w-2xl mx-auto" data-id="element-214">
          {/* Checkout panel */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 md:p-10" data-id="element-215">
            <div className="text-center mb-8" data-id="element-216">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-217">
                Elegí tu Pack:
              </h2>
              <p className="text-lg text-gray-500 font-medium" data-id="element-218">
                Ambas opciones incluyen los 6 Bonus estratégicos
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-219">
              {/* Pack Profesional row */}
              <button type="button" onClick={() => setSelected('pro')} aria-pressed={selected === 'pro'} className={`w-full text-left flex items-center gap-4 p-5 rounded-2xl border-2 transition-all ${selected === 'pro' ? 'border-[#0284C7] bg-[#0284C7]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-220">
                <span className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center ${selected === 'pro' ? 'border-[#0284C7]' : 'border-gray-300'}`} data-id="element-221">
                  {selected === 'pro' && <span className="w-3.5 h-3.5 rounded-full bg-[#0284C7]" data-id="element-222" />}
                </span>
                <div className="flex-grow" data-id="element-223">
                  <div className="flex items-center gap-2" data-id="element-224">
                    <span className="text-xl" data-id="element-225">📘</span>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 uppercase leading-tight" data-id="element-226">
                      Pack Profesional
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 font-medium mt-1" data-id="element-227">
                    16 Tomos + 6 Bonus
                  </p>
                </div>
                <div className="text-right flex-shrink-0" data-id="element-228">
                  <p className="text-gray-400 line-through text-sm font-medium" data-id="element-229">
                    $29.990
                  </p>
                  <p className="text-2xl md:text-3xl font-bold text-gray-900 font-['Anton'] tracking-wide" data-id="element-230">
                    $18.990
                  </p>
                </div>
              </button>

              {/* Megapack Élite row */}
              <button type="button" onClick={() => setSelected('elite')} aria-pressed={selected === 'elite'} className={`w-full text-left flex items-start gap-4 p-5 rounded-2xl border-2 transition-all ${selected === 'elite' ? 'border-[#F97316] bg-[#F97316]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-231">
                <span className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center mt-1 ${selected === 'elite' ? 'border-[#F97316]' : 'border-gray-300'}`} data-id="element-232">
                  {selected === 'elite' && <span className="w-3.5 h-3.5 rounded-full bg-[#F97316]" data-id="element-233" />}
                </span>
                <div className="flex-grow" data-id="element-234">
                  <div className="flex items-center gap-2 mb-2" data-id="element-235">
                    <span className="text-xl" data-id="element-236">🏆</span>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 uppercase leading-tight" data-id="element-237">
                      Megapack Élite
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-238">
                    <span className="bg-[#F97316] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-239">
                      ⭐ Más elegido
                    </span>
                    <span className="bg-[#F97316]/15 text-[#EA580C] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-240">
                      Ahorrás $22.000
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 font-medium" data-id="element-241">
                    16 Tomos + 6 Bonus + 2 EBOOKS ÉLITE
                  </p>
                </div>
                <div className="text-right flex-shrink-0" data-id="element-242">
                  <p className="text-gray-400 line-through text-sm font-medium" data-id="element-243">
                    $49.990
                  </p>
                  <p className="text-2xl md:text-3xl font-bold text-[#F97316] font-['Anton'] tracking-wide" data-id="element-244">
                    $27.990
                  </p>
                </div>
              </button>
            </div>

            <a href={CHECKOUT_URLS[selected]} onClick={() => {
            try {
              ;
              (window as any).fbq?.('track', 'InitiateCheckout', {
                value: selected === 'elite' ? 27990 : 18990,
                currency: 'ARS',
                content_name: selected === 'elite' ? 'Megapack Élite' : 'Pack Profesional'
              });
            } catch (e) {
              /* noop */
            }
          }} className="block w-full text-center py-5 rounded-2xl bg-[#F97316] hover:bg-[#EA580C] text-white font-bold text-lg md:text-2xl shadow-[0_10px_25px_-5px_rgba(249,115,22,0.5)] transition-all hover:scale-[1.02] active:scale-95 uppercase tracking-wide" data-id="element-245">
              {ctaLabel}
            </a>

            <p className="text-center text-gray-400 italic mt-6" data-id="element-246">
              La opción más elegida por entrenadores de hockey argentinos
            </p>
          </div>

          {/* Trust row */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-10 px-2" data-id="element-247">
            <div className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-248">
              <Lock className="w-5 h-5 text-[#0284C7] flex-shrink-0" data-id="element-249" />
              <span data-id="element-250">Pago seguro</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-251">
              <Zap className="w-5 h-5 text-[#F97316] flex-shrink-0" data-id="element-252" />
              <span data-id="element-253">Acceso en 5 min</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-254">
              <ShieldCheck className="w-5 h-5 text-[#0284C7] flex-shrink-0" data-id="element-255" />
              <span data-id="element-256">Garantía 7 días</span>
            </div>
            <div className="flex items-center gap-3 text-gray-700 font-medium" data-id="element-257">
              <CreditCard className="w-5 h-5 text-[#0284C7] flex-shrink-0" data-id="element-258" />
              <span data-id="element-259">Tarjeta · Transferencia</span>
            </div>
          </div>
        </div>
      </section>
    </div>;
}